import {
  AgreementsApi,
  CascadeFileNameEnum,
  CascadesApi,
  Configuration,
  InvoiceFileNameEnum,
  InvoicesApi,
  SaleFileNameEnum,
  SalesApi,
  StatementFileNameEnum,
  StatementsApi,
} from '@sr-sdks/api-sdk-axios';
import { configuration } from '../../configuration';
import axiosInstance from '../axiosInstance';

// Function for creating an instance of the UsersApi class
const GetCascadeApi = () => {
  return new CascadesApi(
    new Configuration(),
    configuration.PAYMENTS_API_BASE,
    axiosInstance,
  );
};

const GetInvoiceApi = () => {
  return new InvoicesApi(
    new Configuration(),
    configuration.PAYMENTS_API_BASE,
    axiosInstance,
  );
};

const GetStatementApi = () => {
  return new StatementsApi(
    new Configuration(),
    configuration.PAYMENTS_API_BASE,
    axiosInstance,
  );
};

const GetAgreementApi = () => {
  return new AgreementsApi(
    new Configuration(),
    configuration.SERVICES_API_BASE,
    axiosInstance,
  );
};

const GetSaleApi = () => {
  return new SalesApi(
    new Configuration(),
    configuration.SERVICES_API_BASE,
    axiosInstance,
  );
};

const downloadFile = (documentDownload: Blob, fileName: string) => {
  if (documentDownload) {
    const url = URL.createObjectURL(
      new Blob([documentDownload], {
        type: documentDownload.type,
      }),
    );

    const link = document.createElement('a');

    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    // Not straight after the click: some browsers still read the URL while the
    // save starts. Revoking frees the Blob, which for a CSV export is the file.
    setTimeout(() => URL.revokeObjectURL(url), 30_000);
  }
};

/**
 * SDK download endpoints return a short-lived redirect URL; fetch that URL as a
 * blob. Shared so redirect/blob bugs are fixed in one place.
 *
 * SDK typings declare `data` as `void` for these endpoints; runtime payload is
 * `{ redirectUrl }`, so we accept `unknown` and narrow here.
 *
 * Pre-HTTP failures (e.g. missing redirect URL) are reported to the global
 * ErrorModal here — they never reach the axios response interceptor.
 */
const reportDownloadError = async (message: string): Promise<never> => {
  const { reduxServices } = await import('../../reducers/reduxServices');

  reduxServices.setCurrentError({ message });
  reduxServices.showErrorModal(true);
  throw new Error(message);
};

const downloadViaRedirect = async (
  apiCall: () => Promise<{ data: unknown }>,
): Promise<Blob> => {
  const { data } = await apiCall();
  const redirectUrl =
    data && typeof data === 'object' && 'redirectUrl' in data
      ? (data as { redirectUrl?: string }).redirectUrl
      : undefined;

  if (!redirectUrl) {
    return reportDownloadError('Download failed: redirect URL missing');
  }

  const blob = await axiosInstance.get(redirectUrl, {
    responseType: 'blob',
  });

  return blob.data as Blob;
};

const agreement = (id: string): Promise<Blob> =>
  downloadViaRedirect(() =>
    GetAgreementApi().agreementControllerFindOneDownload(id),
  );

const sale = (fileName: SaleFileNameEnum, id: string): Promise<Blob> =>
  downloadViaRedirect(() =>
    GetSaleApi().saleControllerFindOneDownload(fileName, id),
  );

const activityStatement = (
  fileName: CascadeFileNameEnum,
  id: string,
): Promise<Blob> =>
  downloadViaRedirect(() =>
    GetCascadeApi().cascadeControllerFindOneDownload(fileName, id),
  );

const paymentStatement = (
  fileName: InvoiceFileNameEnum,
  id: string,
): Promise<Blob> =>
  downloadViaRedirect(() =>
    GetInvoiceApi().invoiceControllerFindOneDownload(fileName, id),
  );

/**
 * Download a royalty statement PDF/CSV via the statements download endpoint.
 * Confirm-toast UX is deferred to APP-2140.
 */
const royaltyStatement = (
  fileName: StatementFileNameEnum,
  statementId: string,
): Promise<Blob> =>
  downloadViaRedirect(() =>
    GetStatementApi().statementControllerFindOneDownload(fileName, statementId),
  );

/**
 * Thin wrapper around {@link paymentStatement} so APP-2140 can add confirm-toast
 * UX for royalty invoice downloads without changing non-royalty call sites.
 */
const royaltyInvoice = (
  fileName: InvoiceFileNameEnum,
  id: string,
): Promise<Blob> => paymentStatement(fileName, id);

const downloadApi = {
  activityStatement,
  agreement,
  downloadFile,
  paymentStatement,
  royaltyInvoice,
  royaltyStatement,
  sale,
};

export default downloadApi;
